'use client';

import React, { useEffect, useState } from 'react';
import { FormField } from '@/sanity.types';
import { generateZodSchema } from '../libs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { FormFieldRender } from './formFields';
import Button from '@/components/button/button';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { PortableTextBlock } from 'next-sanity';
import { usePathname } from 'next/navigation';

interface FormWrapperProps {
  formTitle?: string;
  formDescription?: PortableTextBlock[];
  apiEndpoint: string;
  formFields:
    | ({
        _key: string;
      } & FormField)[]
    | undefined;
}

export const FormWrapper = ({
  apiEndpoint,
  formFields,
  formTitle,
  formDescription,
}: FormWrapperProps) => {
  const schema = generateZodSchema({
    fields: formFields,
    submitEndpoint: apiEndpoint,
  });
  type FormData = z.infer<typeof schema>;
  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (successMessage) {
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    }
  }, [successMessage]);

  const onSubmit = (data: FormData) => {
    setIsSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    // Transform keys for share-your-story
    let payload: Record<string, unknown> = { ...data };
    if (pathname.includes('share-your-story')) {
      payload = {
        'entry.241268794': data.name,
        'entry.2118221368': data.email,
        'entry.1066028566': data.city,
        'entry.1764002160': data.org,
        'entry.1742916747': data.orgType,
        'entry.865379055': data.message,
      };
    }

    const formData = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (typeof value !== 'undefined' && value !== null) {
        formData.append(key, String(value));
      }
    });

    fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      mode: 'no-cors',
    })
      .then(() => {
        methods.reset();
        const isShareYourStory = pathname.includes('share-your-story');
        setSuccessMessage(
          isShareYourStory
            ? 'Thanks for sharing your story!'
            : 'Form submitted successfully'
        );
        setIsSubmitting(false);
      })
      .catch(() => {
        const isShareYourStory = pathname.includes('share-your-story');
        setErrorMessage(
          isShareYourStory
            ? 'Sorry, something went wrong! Please reach out for <a href="/help">help here</a>.'
            : 'There was an error submitting the form.'
        );
        setIsSubmitting(false);
      });
  };

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form-builder-form-wrap"
      >
        {formTitle && <h2>{formTitle}</h2>}
        {formDescription && (
          <div className="sub-description">
            <LandingPortableText value={formDescription} />
          </div>
        )}
        {formFields?.map((field) => {
          return <FormFieldRender field={field} key={field.fieldName} />;
        })}

        {successMessage && (
          <div className="mt-3 alert alert-success">{successMessage}</div>
        )}
        {errorMessage && (
          <div
            className="mt-3 alert alert-danger"
            dangerouslySetInnerHTML={{ __html: errorMessage }}
          />
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className={isSubmitting ? 'loading' : ''}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Form>
    </FormProvider>
  );
};
